import { Command } from 'commander';
import chalk from 'chalk';
import { readFileSync, existsSync } from 'fs';
import { ApiError, MutagentError } from '../../lib/errors.js';
import { getSDKClient } from '../../lib/sdk-client.js';
import { renderPlainCard } from '../../lib/ui-links.js';
import { buildScorecardDetailsText, type ScorecardDetailData } from '../../lib/scorecard-details.js';
import { MutationContext } from '../../lib/mutation-context.js';
import { registerPromptsCrud } from './prompts-crud.js';
import { registerDatasetCommands } from './datasets.js';
import { registerEvaluationCommands } from './evaluations.js';
import { registerOptimizeCommands } from './optimize.js';
import { registerExperimentCommands } from './experiments.js';

export { parseDatasetFile } from './datasets.js';

// Command option interfaces for type safety
export interface ListOptions {
  limit: string;
}

export interface GetOptions {
  withDatasets?: boolean;
  withEvals?: boolean;
}

export interface CreateOptions {
  name?: string;
  description?: string;
  content?: string;
  outputSchema?: string;
  inputSchema?: string;
  // Prompt style flags
  raw?: string;
  system?: string;
  human?: string;
  messages?: string;
}

export interface UpdateOptions {
  name?: string;
  description?: string;
  content?: string;
  // Prompt style flags
  raw?: string;
  system?: string;
  human?: string;
  messages?: string;
  // Schema flags
  inputSchema?: string;
  inputSchemaFile?: string;
  outputSchema?: string;
  outputSchemaFile?: string;
}

export interface DeleteOptions {
  force?: boolean;
}

/**
 * Silently update .mutagent/mutation-context.md.
 * Never throws — context update failures should not break CLI commands.
 */
export function updateMutationContext(updater: (ctx: MutationContext) => void): void {
  try {
    const ctx = MutationContext.load();
    updater(ctx);
    ctx.save();
  } catch {
    // Silent — context update is best-effort
  }
}

/**
 * Parse API validation errors into human-readable messages.
 * The API returns structured errors like { message: '...', errors: [...] }
 */
export function parseValidationErrors(error: unknown): string[] {
  if (error instanceof ApiError) {
    try {
      const body = JSON.parse(error.message) as { errors?: { message?: string }[]; message?: string };
      if (body.errors && Array.isArray(body.errors)) {
        return body.errors.map((e: { message?: string }) => e.message ?? JSON.stringify(e));
      }
      if (body.message) {
        return [body.message];
      }
    } catch {
      // Not JSON, return the message as-is
      return [error.message];
    }
  }
  if (error instanceof Error) {
    return [error.message];
  }
  return ['An unknown error occurred'];
}

/**
 * Check if an inputSchema or outputSchema is effectively empty.
 * Returns true if the value is undefined, null, an empty object {}, or
 * an object with no "properties" key (which means no defined variables).
 */
export function isSchemaEmpty(schema: unknown): boolean {
  if (schema === undefined || schema === null) return true;
  if (typeof schema !== 'object') return false;
  const obj = schema as Record<string, unknown>;
  // Empty object {}
  if (Object.keys(obj).length === 0) return true;
  return false;
}

/**
 * Parse a schema option provided as either inline JSON or file path.
 * Returns the parsed schema or undefined if neither is provided.
 * Throws MutagentError on conflict, file-not-found, or parse errors.
 */
export function parseSchemaOption(
  inlineJson: string | undefined,
  filePath: string | undefined,
  schemaName: string,
  helpCommand: string,
): Record<string, unknown> | undefined {
  if (inlineJson && filePath) {
    throw new MutagentError(
      'INVALID_ARGUMENTS',
      `Cannot use --${schemaName} and --${schemaName}-file together`,
      `Run: ${helpCommand}\nUse --${schemaName} for inline JSON or --${schemaName}-file for file-based input, not both`
    );
  }
  if (inlineJson) {
    try {
      return JSON.parse(inlineJson) as Record<string, unknown>;
    } catch {
      throw new MutagentError(
        'INVALID_JSON',
        `Invalid JSON in --${schemaName} flag`,
        `Run: ${helpCommand}\nProvide a valid JSON Schema, e.g., '{"type":"object","properties":{"field":{"type":"string"}}}'`
      );
    }
  }
  if (filePath) {
    if (!existsSync(filePath)) {
      throw new MutagentError(
        'FILE_NOT_FOUND',
        `File not found: ${filePath}`,
        `Run: ${helpCommand}\nCheck the file path and try again`
      );
    }
    try {
      return JSON.parse(readFileSync(filePath, 'utf-8')) as Record<string, unknown>;
    } catch {
      throw new MutagentError(
        'INVALID_JSON',
        `Failed to parse JSON from ${filePath}`,
        `Run: ${helpCommand}\nEnsure the file contains valid JSON Schema`
      );
    }
  }
  return undefined;
}

/**
 * Build a plain-text scorecard for optimization results directives.
 */
export function buildResultsScorecardText(resultData: Record<string, unknown>): string {
  const jobData = resultData.job as Record<string, unknown> | undefined;
  const jobId = typeof jobData?.id === 'string' ? jobData.id : 'N/A';
  const jobStatus = typeof jobData?.status === 'string' ? jobData.status : 'unknown';
  const originalScore = resultData.originalScore as number | undefined;
  const bestScore = resultData.bestScore as number | undefined;
  const iterations = (resultData.iterationsCompleted as number | undefined) ?? 0;

  const title = 'Optimization Results';
  const rows = [
    { label: 'Job ID', value: jobId },
    { label: 'Status', value: jobStatus },
    { label: 'Iterations', value: String(iterations) },
    { label: 'Original Score', value: originalScore !== undefined ? originalScore.toFixed(2) : 'N/A' },
    { label: 'Best Score', value: bestScore !== undefined ? bestScore.toFixed(2) : 'N/A' },
  ];
  if (originalScore !== undefined && bestScore !== undefined && originalScore > 0) {
    const pct = Math.round(((bestScore - originalScore) / originalScore) * 100);
    rows.push({ label: 'Improvement', value: `${pct > 0 ? '+' : ''}${String(pct)}%` });
  }

  let text = renderPlainCard({ title, rows, links: [], next: [] });

  // Prompt comparison (full text)
  const originalPrompt = resultData.originalPrompt as Record<string, unknown> | undefined;
  const prompt = resultData.prompt as Record<string, unknown> | undefined;
  const originalText = (originalPrompt?.systemPrompt ?? originalPrompt?.rawPrompt ?? '') as string;
  const optimizedText = (prompt?.systemPrompt ?? prompt?.rawPrompt ?? prompt?.humanPrompt ?? '') as string;
  if (originalText || optimizedText) {
    const lines: string[] = ['', 'Prompt Comparison', '\u2500'.repeat(70)];
    lines.push('BEFORE:');
    for (const pLine of (originalText || '(empty)').split('\n')) {
      lines.push(`  ${pLine}`);
    }
    lines.push(`Length: ${String(originalText.length)} chars`);
    lines.push('');
    lines.push('AFTER:');
    for (const pLine of (optimizedText || '(empty)').split('\n')) {
      lines.push(`  ${pLine}`);
    }
    lines.push(`Length: ${String(optimizedText.length)} chars`);
    const growth = optimizedText.length - originalText.length;
    lines.push(`Growth: ${growth >= 0 ? '+' : ''}${String(growth)} chars`);
    text += '\n' + lines.join('\n');
  }

  // Extended detail sections
  text += buildScorecardDetailsText(resultData as unknown as ScorecardDetailData);

  return text;
}

/**
 * Pre-flight check ensuring the workspace has at least one LLM provider configured.
 * Runs unconditionally in the default (BYOK) mode. Self-hosted deployments can
 * set MUTAGENT_EXT_PROVIDER=false to skip this check when the server resolves
 * credentials internally.
 *
 * @throws {MutagentError} NO_PROVIDER_CONFIGURED when no providers are found
 */
export async function checkProviderConfigured(): Promise<void> {
  // Escape hatch for self-hosted deployments where the server resolves provider
  // credentials internally (USE_EXT_PROVIDERS=false on the BE). Unified env var
  // name with the BE flag for operator clarity.
  if (process.env.MUTAGENT_EXT_PROVIDER === 'false') {
    return;
  }
  const client = await getSDKClient();
  const providers = await client.listProviders();
  if (!providers.data || providers.data.length === 0) {
    throw new MutagentError(
      'NO_PROVIDER_CONFIGURED',
      'No LLM provider configured for this workspace.',
      'Run: mutagent providers add --provider openai --name "My OpenAI" --api-key $KEY  |  Or visit Settings → Providers  |  Self-hosted? Set MUTAGENT_EXT_PROVIDER=false'
    );
  }
}

export const PREREQUISITES_TEXT = `
${chalk.red('Prerequisites (required):')}
  1. Evaluation criteria defined  ${chalk.dim('(via dashboard or evaluation create)')}
  2. Dataset uploaded             ${chalk.dim('mutagent prompts dataset list <prompt-id>')}
  3. LLM provider configured     ${chalk.dim('Settings → Providers  or  mutagent providers add ...')}`;

export function createPromptsCommand(): Command {
  const prompts = new Command('prompts')
    .description('Manage prompts, datasets, evaluations, and optimizations')
    .addHelpText('after', `
Examples:
  ${chalk.dim('$')} mutagent prompts list
  ${chalk.dim('$')} mutagent prompts get <prompt-id>
  ${chalk.dim('$')} mutagent prompts create --name "my-prompt" --system "You are helpful" --human "{input}"
  ${chalk.dim('$')} mutagent prompts dataset list <prompt-id>
  ${chalk.dim('$')} mutagent prompts evaluation create <prompt-id> --name "My Eval"
  ${chalk.dim('$')} mutagent prompts optimize start <prompt-id> --dataset <dataset-id> --evaluation <eval-id>

Subcommands:
  list, get, create, update, delete
  dataset list|add|delete
  evaluation list|create|delete
  optimize start|status|results
    `);

  registerPromptsCrud(prompts);
  registerDatasetCommands(prompts);
  registerEvaluationCommands(prompts);
  registerOptimizeCommands(prompts);
  registerExperimentCommands(prompts);

  return prompts;
}
